import axios, { parseAxiosError } from '@/portainer/services/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { Catalog } from './types/registry';
import { Repository } from './repositories/ListView/types';

interface PaginationParams {
  last?: string;
  n?: string;
}

interface GitlabRepository {
  id: number;
  name: string;
  path: string;
  project_id: number;
  location: string;
  created_at: string;
  tags: Record<string, unknown>[];
}

// This is a bit of a hack, but it's the only way to get the pagination info from the response
export function parseLinkHeader(linkHeader: string): {
  n?: string;
  last?: string;
} {
  if (!linkHeader) return {};

  const queryString = linkHeader
    .substring(linkHeader.indexOf('?') + 1)
    .split('>;')[0];
  const queries = queryString.split('&');
  const result: { n?: string; last?: string } = {};

  for (let i = 0; i < queries.length; i++) {
    const kv = queries[i].split('=');
    const key = kv[0];
    const value = kv[1];
    if (key === 'n' || key === 'last') {
      result[key] = value;
    }
  }

  return result;
}

export async function getAllRepositories(
  registryId: number,
  environmentId?: EnvironmentId,
  paginationParams?: PaginationParams,
  accumulatedRepos: string[] = []
): Promise<Repository[]> {
  try {
    const { data, headers } = await axios.get<Catalog>(
      `/registries/${registryId}/v2/_catalog`,
      {
        params: {
          ...paginationParams,
          ...(environmentId ? { endpointId: environmentId } : undefined),
        },
        headers: {
          Accept: 'application/json',
        },
        maxRedirects: 5, // Follow redirects
      }
    );

    // Accumulate repositories from this page
    const allRepos = [...accumulatedRepos, ...(data.repositories || [])];

    // Parse Link header for pagination
    const linkHeader = headers.link;
    if (linkHeader) {
      const paginationInfo = parseLinkHeader(linkHeader);

      // If we have pagination info, fetch the next page
      if (paginationInfo.n && paginationInfo.last) {
        return await getAllRepositories(
          registryId,
          environmentId,
          paginationInfo,
          allRepos
        );
      }
    }

    return allRepos.map((repo) => ({ Name: repo }));
  } catch (err) {
    throw parseAxiosError(err as Error, 'Failed to get catalog of registry');
  }
}

export async function getAllGitlabRepositories(
  registryId: number,
  projectId: number,
  environmentId?: EnvironmentId,
  page: number = 1,
  accumulatedRepos: GitlabRepository[] = []
): Promise<Repository[]> {
  try {
    const { data, headers } = await axios.get<GitlabRepository[]>(
      `/registries/${registryId}/proxies/gitlab/api/v4/projects/${projectId}/registry/repositories`,
      {
        params: {
          tags: true,
          page,
          ...(environmentId ? { endpointId: environmentId } : undefined),
        },
      }
    );

    // Accumulate repositories
    const allRepos = [...accumulatedRepos, ...data];

    // Check for next page using GitLab's X-Next-Page header
    const nextPageHeader = headers['x-next-page'];

    // If there's a next page, recursively fetch it
    if (nextPageHeader) {
      const nextPage = parseInt(nextPageHeader, 10);
      return await getAllGitlabRepositories(
        registryId,
        projectId,
        environmentId,
        nextPage,
        allRepos
      );
    }

    // Filter out repositories with no tags and transform to Repository objects
    const repositories = allRepos
      .filter((repo) => repo.tags && repo.tags.length > 0)
      .map((repo) => ({ Name: repo.path }));

    return repositories;
  } catch (err) {
    throw parseAxiosError(err as Error, 'Failed to get GitLab repositories');
  }
}
